/// <amd-module name="Wit/QueriesHub/Editor/EditorTable" />

import { ITfsProjectService } from "TFS/Services/TfsProjectService";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer, newGuid, startsWith } from "VSS/Core/Util/String";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { localeDateFormat } from "VSS/Platform/Date";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { IIdentity, IdentityPicker, IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ITableColumn, SimpleTableCell, Table, TableHeaderCell } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { WorkItemTypeCategory } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work.Types";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { Dictionary } from "Wit/Common/Dictionary";
import { CoreFieldRefNames, FieldFlags, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { checkFlag } from "Wit/Common/QueryUtils/QueryUtils";
import { getAllowedIdentityTypes } from "Wit/Common/Utilities/Identity";
import { WITMeMacroPeoplePickerProvider } from "Wit/Common/WITMeMacroPeoplePickerProvider";
import {
    AreaPathOperatorsSupportingMacros,
    ComparisonOperators,
    DateMacros,
    EqualityOperators,
    FieldTypesSupportingEver,
    FieldTypesSupportingFieldComparison,
    HistoryOperators,
    HistoryWithTextSupportOperators,
    IdInNotInMacros,
    IterationPathMacros,
    IterationPathOperatorsSupportingMacros,
    OperatorsSupportingFieldComparison,
    StringOperators,
    StringWithInGroupOperators,
    StringWithTextSupportOperators,
    TextOperators,
    TextWithTextSupportOperators,
    TreePathOperators,
    WiqlOperators,
    getFieldComparisonOperator,
    getInvariantOperator,
    getLocalizedOperator,
    getLocalizedOperatorList,
    isContainsNotContainsOperator,
    isCurrentIterationMacro,
    isFieldComparisonOperator,
    isGroupOperator,
    isMultipleValueOperator,
    isNonNullableField,
    isTeamAreasMacro,
    isValidMacro
} from "Wit/Common/WiqlOperators";
import { parseCurrentIteration, parseTeamAreas } from "Wit/Common/WiqlValues";
import { AreaPathDropdown } from "Wit/Common/WorkItemControls/AreaPathDropdown";
import { DatePickerDialog } from "Wit/Common/WorkItemControls/DatePickerDialog";
import { IterationDropdown } from "Wit/Common/WorkItemControls/IterationDropdown";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { getAADGroupName, getIIdentityObject, isAADGroupIdentity } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { ClauseType, IClause, IEditInfo, IFilter, IFilterGroup, IQueryEditorItem, InputMode } from "Wit/QueriesHub/Common/Interfaces";
import * as Resources from "Wit/QueriesHub/Resources";
import { IFieldProjectData } from "Wit/WorkItemDataManager/Contracts";
import * as React from "react";

export interface IEditorTableProps {
    clauseType: ClauseType;
    crossProject: boolean;
    editInfo: IEditInfo;
    fieldProjectData: IFieldProjectData;
    fields: IFieldEntry[];
    projectId: string;
    setIsDirty: (isDirty: boolean) => void;
    setFilterState: (filter: IFilter, clauseType: ClauseType) => void;
    setStateAddRow: (clauseType: ClauseType, rowIndex?: number) => void;
    setStateRemoveRow: (clauseType: ClauseType, rowIndex: number) => void;
    setClauseFieldState: (field: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseOperatorState: (operator: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseValueState: (value: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseLogicalOperatorState: (logicalOperator: string, rowIndex: number, clauseType: ClauseType) => void;
}

interface FieldsAllowedValuesData {
    data: string[];
    errorMessage: string;
}

interface FieldAllowedValuesRequestParams {
    projectId?: string;
    fieldRefName: string;
}

interface TeamsData {
    id: string;
    name: string;
    identityUrl: string;
    projectId: string;
    projectName: string;
}

export const getDefaultRow = (
    fieldRefName?: string,
    value?: string,
    andOr?: string,
    operator?: string,
    valueIdentity?: IIdentity,
    valueIdentityList?: IIdentity[],
    valuePathTeam?: string
): IQueryEditorItem => {
    return {
        id: newGuid(),
        isNewItem: false,
        groupClause: new ObservableValue(false),
        andOr: new ObservableValue(andOr?.toLowerCase() ?? "and"),
        andOrSelection: new DropdownSelection(),
        field: new ObservableValue(fieldRefName ?? ""),
        fieldError: new ObservableValue(false),
        fieldSelection: new DropdownSelection(),
        operator: new ObservableValue(operator !== undefined ? operator : getLocalizedOperator(WiqlOperators.OperatorEqualTo)),
        operatorSelection: new DropdownSelection(),
        operatorList: new ObservableArray<IListBoxItem>([{ id: WiqlOperators.OperatorEqualTo, text: WiqlOperators.OperatorEqualTo }]),
        value: new ObservableValue(value !== undefined ? (value === Resources.AnyOptionText ? "[Any]" : value) : ""),
        valueIdentity: new ObservableValue(valueIdentity ?? undefined),
        valueIdentityList: new ObservableArray<IIdentity>(valueIdentityList),
        valueSelection: new DropdownSelection(),
        valueList: new ObservableArray<IListBoxItem>([{ id: "[Any]", text: Resources.AnyOptionText }]),
        valueDropdownInput: new ObservableValue(InputMode.EditableDropdown),
        valuePathTeam: new ObservableValue<string>(valuePathTeam ?? ""),
        valuePathTeamSelection: new DropdownSelection()
    };
};

/**
 * The width/height used for grouping icons/colored squares
 * This aligns with the value defined in QueryEditor.scss
 */
const c_groupingIconWidth = 25;
/**
 * The id of the custom date row for the DatePickerDropdown
 */
const c_customDateId = "custom-date";

export class EditorTable extends VssComponent<IEditorTableProps> {
    private fieldRefNameToValueListMap: Dictionary<IObservableArray<IListBoxItem>> = {};
    private fieldRefNameToValueListMap_CrossProject: Dictionary<IObservableArray<IListBoxItem>> = {};
    private pickerProvider: IPeoplePickerProvider = new WITMeMacroPeoplePickerProvider(this.context.pageContext, {
        identityTypes: getAllowedIdentityTypes(this.context.pageContext)
    });
    private groupPickerProvider: IPeoplePickerProvider = new PeoplePickerProvider(this.context.pageContext, {
        identityTypes: ["group"]
    });
    private witRestClient: IWorkItemTrackingRestClient;
    private workRestClient: IWorkRestClient;
    private rows: ObservableArray<IQueryEditorItem> = new ObservableArray<IQueryEditorItem>();
    private teamsData: ObservableArray<TeamsData> = new ObservableArray<TeamsData>();
    private teamsData_CrossProject: ObservableArray<TeamsData> = new ObservableArray<TeamsData>();
    private workItemFieldList: ObservableArray<IListBoxItem> = new ObservableArray<IListBoxItem>();

    // Group clause
    private disableGroupClauseButton: ObservableValue<boolean>;
    private groupedRowIndices: number[] = [];
    private currentFilterGroup: ObservableValue<IFilterGroup>;
    private allFilterGroups: ObservableValue<Map<number, IFilterGroup[]>> = new ObservableValue<Map<number, IFilterGroup[]>>(new Map());
    private groupClauseWidth: ObservableValue<number> = new ObservableValue(c_groupingIconWidth);

    private hasTags: boolean = true;
    private hasRows: boolean = true;
    private workItemTypeCategories: ObservableArray<WorkItemTypeCategory> = new ObservableArray<WorkItemTypeCategory>();

    constructor(props: IEditorTableProps, context: IVssComponentContext) {
        super(props, context);
        this.witRestClient = getWorkItemTrackingClient(this.context.pageContext);
        this.workRestClient = getWorkClient(this.context.pageContext);
        const fieldList: IListBoxItem[] = [];
        if (props.crossProject) {
            props.fields.forEach(field => {
                fieldList.push({ id: field.referenceName, text: field.name });
            });
        } else {
            props.fieldProjectData.fields.forEach(field => {
                fieldList.push({ id: field.referenceName, text: field.name });
            });
        }
        this.workItemFieldList.value = fieldList;
        if (props.clauseType === ClauseType.Source) {
            if (props.editInfo.sourceFilter && props.editInfo.sourceFilter.clauses && props.editInfo.sourceFilter.clauses.length !== 0) {
                this.processBasicQueryRows(props.editInfo.sourceFilter);
            } else {
                this.getDefaultRowsForNewQuery();
            }
        } else if (props.clauseType === ClauseType.Link) {
            if (props.editInfo.linkTargetFilter && props.editInfo.linkTargetFilter.clauses && props.editInfo.linkTargetFilter.clauses.length !== 0) {
                this.processBasicQueryRows(props.editInfo.linkTargetFilter);
            } else {
                this.getDefaultRowsForNewQuery();
            }
        } else {
            if (props.editInfo.treeTargetFilter && props.editInfo.treeTargetFilter.clauses && props.editInfo.treeTargetFilter.clauses.length !== 0) {
                this.processBasicQueryRows(props.editInfo.treeTargetFilter);
            } else {
                this.getDefaultRowsForNewQuery();
            }
        }
        this.rows.value.map((row, index) => {
            if (row.field.value.length > 0) {
                row.operatorList.value = this.getListBoxItems(this.getOperators(row.field.value));
                this.updateValueListBasedOnOp(index, row.field.value, false);
            }
        });
        this.disableGroupClauseButton = new ObservableValue<boolean>(true);
        this.currentFilterGroup = new ObservableValue<IFilterGroup>({ start: -1, end: -1, level: 0 });
    }

    public render(): JSX.Element {
        return (
            <Card className="flex-column bolt-table-card" contentProps={{ contentPadding: false }}>
                <div className="flex-column flex-grow rhythm-vertical-8 padding-bottom-8">
                    <Table className="query-editor-table font-size" columns={this.columns} itemProvider={this.rows} showLines={false} />
                    <div className="padding-horizontal-8">
                        <Button
                            className="add-new-clause-button"
                            subtle={true}
                            iconProps={{ className: "add-new-clause-icon-color", iconName: "Add" }}
                            text={Resources.AddNewClauseLabel}
                            onClick={() => this.onAddRow()}
                        />
                    </div>
                </div>
            </Card>
        );
    }

    public componentDidUpdate(prevProps: IEditorTableProps, prevState: {}, snapshot: {}) {
        super.componentDidUpdate(prevProps, prevState, snapshot);
        let updateValues = false;
        let updateOperators = false;
        if (this.props.crossProject !== prevProps.crossProject) {
            const updatedFieldList: IListBoxItem[] = [];
            if (this.props.crossProject) {
                this.props.fields.forEach(field => {
                    updatedFieldList.push({ id: field.referenceName, text: field.name });
                });
            } else {
                this.props.fieldProjectData.fields.forEach(field => {
                    updatedFieldList.push({ id: field.referenceName, text: field.name });
                });
            }
            this.workItemFieldList.value = updatedFieldList;
            updateValues = true;
        }
        const clauseType = this.props.clauseType;
        // editInfo may change in cases where the wiql generator simplifies the query/removes redundant clauses or groups after running a query
        if (clauseType === prevProps.clauseType && this.hasEditInfoChanged(this.props.editInfo, prevProps.editInfo)) {
            this.processBasicQueryRows(
                clauseType === ClauseType.Source
                    ? this.props.editInfo.sourceFilter
                    : clauseType === ClauseType.Link
                    ? this.props.editInfo.linkTargetFilter
                    : this.props.editInfo.treeTargetFilter
            );
            updateValues = true;
            updateOperators = true;
        }
        if (updateValues) {
            // after updating the list of field, operator and allowed values data
            this.rows.value.forEach((row, index) => {
                if (row.field.value.length > 0) {
                    // find the index of the selected field in the field list, and update the fieldSelection accordingly
                    const selectedIndex = this.workItemFieldList.value.findIndex(field => field.id === row.field.value);
                    row.fieldSelection.select(selectedIndex > -1 ? selectedIndex : 0);
                    row.fieldError.value =
                        (row.fieldSelection.value[0].beginIndex === -1 || row.fieldSelection.value[0].endIndex === -1) &&
                        row.field.value !== CoreFieldRefNames.TeamProject;
                    if (updateOperators) {
                        // update operator list
                        row.operatorList.value = this.getListBoxItems(this.getOperators(row.field.value));
                        const opIndex = row.operatorList.value.findIndex(op => op.id === row.operator.value);
                        opIndex !== -1 ? row.operatorSelection.select(opIndex) : row.operatorSelection.select(0);
                    }
                    // update the list of allowed values
                    this.updateValueListBasedOnOp(index, row.field.value, false);
                }
            });
        }
    }

    private hasEditInfoChanged = (currEditInfo: IEditInfo, prevEditInfo: IEditInfo): boolean => {
        if (this.props.clauseType === ClauseType.Source) {
            if (
                currEditInfo.sourceFilter.clauses.length !== prevEditInfo.sourceFilter.clauses.length ||
                currEditInfo.sourceFilter.groups.length !== prevEditInfo.sourceFilter.groups.length
            ) {
                return true;
            } else {
                return (
                    this.hasClauseChanged(currEditInfo.sourceFilter.clauses, prevEditInfo.sourceFilter.clauses) ||
                    this.hasGroupingChanged(currEditInfo.sourceFilter.groups, prevEditInfo.sourceFilter.groups)
                );
            }
        } else if (this.props.clauseType === ClauseType.Link) {
            if (
                currEditInfo.linkTargetFilter.clauses.length !== prevEditInfo.linkTargetFilter.clauses.length ||
                currEditInfo.linkTargetFilter.groups.length !== prevEditInfo.linkTargetFilter.groups.length
            ) {
                return true;
            } else {
                return (
                    this.hasClauseChanged(currEditInfo.linkTargetFilter.clauses, prevEditInfo.linkTargetFilter.clauses) ||
                    this.hasGroupingChanged(currEditInfo.linkTargetFilter.groups, prevEditInfo.linkTargetFilter.groups)
                );
            }
        } else {
            if (
                currEditInfo.treeTargetFilter.clauses.length !== prevEditInfo.treeTargetFilter.clauses.length ||
                currEditInfo.treeTargetFilter.groups.length !== prevEditInfo.treeTargetFilter.groups.length
            ) {
                return true;
            } else {
                return (
                    this.hasClauseChanged(currEditInfo.treeTargetFilter.clauses, prevEditInfo.treeTargetFilter.clauses) ||
                    this.hasGroupingChanged(currEditInfo.treeTargetFilter.groups, prevEditInfo.treeTargetFilter.groups)
                );
            }
        }
    };

    private hasClauseChanged = (currClause: IClause[], prevClause: IClause[]): boolean => {
        for (let i = 0; i < currClause.length; i++) {
            if (
                (i !== 0 && currClause[i].logicalOperator !== prevClause[i].logicalOperator) ||
                currClause[i].fieldName !== prevClause[i].fieldName ||
                currClause[i].operator !== prevClause[i].operator ||
                currClause[i].value !== prevClause[i].value
            ) {
                return true;
            }
        }
        return false;
    };

    private hasGroupingChanged = (currGroup: IFilterGroup[], prevGroup: IFilterGroup[]): boolean => {
        for (let i = 0; i < currGroup.length; i++) {
            if (currGroup[i].start !== prevGroup[i].start || currGroup[i].end !== prevGroup[i].end || currGroup[i].level !== prevGroup[i].level) {
                return true;
            }
        }
        return false;
    };

    private get fieldValuesByRefName() {
        return this.props.crossProject ? this.fieldRefNameToValueListMap_CrossProject : this.fieldRefNameToValueListMap;
    }

    private get teamFieldValues() {
        return this.props.crossProject ? this.teamsData_CrossProject : this.teamsData;
    }

    private getFieldName = (fieldRefName: string): string => {
        return this.workItemFieldList.value.find(field => field.id === fieldRefName)?.text ?? "";
    };

    private getDefaultRowsForNewQuery = () => {
        this.rows.push(getDefaultRow(CoreFieldRefNames.ChangedDate, WiqlOperators.MacroToday + " - 180", "", WiqlOperators.OperatorGreaterThan));
        this.rows.push(getDefaultRow(CoreFieldRefNames.WorkItemType, "[Any]"));
        if (this.props.clauseType === ClauseType.Source) {
            this.rows.push(getDefaultRow(CoreFieldRefNames.State, "[Any]"));
        }
        const filter: IFilter = {
            clauses: this.rows.value.map((row, index) => {
                return {
                    fieldName: this.getFieldName(row.field.value),
                    operator: row.operator.value,
                    value: row.value.value,
                    index: index,
                    originalIndex: index,
                    logicalOperator: row.andOr.value === "or" ? "Or" : "And"
                } as IClause;
            }),
            groups: []
        };
        this.props.setFilterState(filter, this.props.clauseType);
    };

    private getFieldRefName = (fieldName: string): string => {
        const field = this.props.fields.find(field => field.name === fieldName);
        if (field) {
            return field.referenceName;
        } else if (!fieldName) {
            // This is expected - the field dropdown hasn't been filled out yet.
            return "";
        } else {
            reportError(this.context.pageContext, "QueryEditor_GetFieldRefName", format(Resources.FieldNotFoundError, fieldName));
            return "";
        }
    };

    private processBasicQueryRows = (filter: IFilter) => {
        this.rows.value =
            filter.clauses.map(clause => {
                const fieldRefName = this.getFieldRefName(clause.fieldName);
                if (this.isIdentityField(fieldRefName)) {
                    if (isMultipleValueOperator(clause.operator)) {
                        const identityList = clause.value
                            .split(",")
                            .map(value => {
                                const sanitizedClause = clause.value.replace("@Me", "@me");
                                const row = this.rows.value.find(
                                    row =>
                                        row.value &&
                                        row.value.value &&
                                        sanitizedClause.includes(row.value.value.replace("@Me", "@me"))
                                );
                                const identity =
                                    row && row?.valueIdentityList.value.length > 0
                                        ? row.valueIdentityList.value.find(item => item.displayName && value.includes(item.displayName))
                                        : undefined;
                                return getIIdentityObject(this.context.pageContext, value, identity?.entityId);
                            })
                            .filter(v => v) as IIdentity[];
                        return getDefaultRow(fieldRefName, clause.value, clause.logicalOperator, clause.operator, undefined, identityList);
                    } else if (isContainsNotContainsOperator(clause.operator)) {
                        return getDefaultRow(fieldRefName, clause.value, clause.logicalOperator, clause.operator);
                    } else {
                        let identity = getIIdentityObject(this.context.pageContext, clause.value);
                        if (!identity?.entityId && clause.value) {
                            // if the identity was not resolved, use only the display name
                            identity = { displayName: clause.value } as IIdentity;
                        }
                        return getDefaultRow(fieldRefName, clause.value, clause.logicalOperator, clause.operator, identity);
                    }
                } else if (fieldRefName === CoreFieldRefNames.AreaPath) {
                    const team = parseTeamAreas(clause.value);
                    if (team) {
                        // this is a macro
                        return getDefaultRow(
                            fieldRefName,
                            WiqlOperators.MacroTeamAreas,
                            clause.logicalOperator,
                            clause.operator,
                            undefined,
                            undefined,
                            team.team
                        );
                    } else {
                        return getDefaultRow(fieldRefName, clause.value, clause.logicalOperator, clause.operator);
                    }
                } else if (fieldRefName === CoreFieldRefNames.IterationPath) {
                    const teamAndOffset = parseCurrentIteration(clause.value, true);
                    if (teamAndOffset) {
                        // this is a macro
                        if (teamAndOffset.offset === 0) {
                            // macro is @CurrentIteration
                            return getDefaultRow(
                                fieldRefName,
                                WiqlOperators.MacroCurrentIteration,
                                clause.logicalOperator,
                                clause.operator,
                                undefined,
                                undefined,
                                teamAndOffset.team
                            );
                        } else {
                            // macro is @CurrentIteration + 1 or @CurrentIteration - 1
                            return getDefaultRow(
                                fieldRefName,
                                WiqlOperators.MacroCurrentIteration + (teamAndOffset.offset > 0 ? " + " : " - ") + Math.abs(teamAndOffset.offset),
                                clause.logicalOperator,
                                clause.operator,
                                undefined,
                                undefined,
                                teamAndOffset.team
                            );
                        }
                    } else {
                        return getDefaultRow(fieldRefName, clause.value, clause.logicalOperator, clause.operator);
                    }
                } else if (this.getFieldType(fieldRefName) === FieldType.DateTime) {
                    let value: string;
                    if (startsWith(clause.value, WiqlOperators.MacroStart) || isNaN(Date.parse(clause.value))) {
                        value = clause.value;
                    } else {
                        value = localeDateFormat(new Date(Date.parse(clause.value)), "d");
                    }
                    return getDefaultRow(fieldRefName, value, clause.logicalOperator, clause.operator, undefined, undefined, undefined);
                } else {
                    return getDefaultRow(fieldRefName, clause.value, clause.logicalOperator, clause.operator);
                }
            }) ?? [];
        if (filter.groups.length !== 0) {
            const groups = filter.groups;
            this.groupClauseWidth.value += c_groupingIconWidth * groups.length;
            const collection = new Map<number, IFilterGroup[]>();
            groups.forEach(group => {
                const level = group.level ?? 0;
                const levelArray = collection.get(level);
                if (levelArray) {
                    levelArray.push(group);
                } else {
                    collection.set(level, [group]);
                }
            });

            this.allFilterGroups.value = collection;
        } else if (this.allFilterGroups.value.size > 0) {
            const widthToRemove = (this.allFilterGroups.value.size + 1) * c_groupingIconWidth;
            this.allFilterGroups.value = new Map<number, IFilterGroup[]>();

            // don't let the width be smaller than the grouping checkboxes
            this.groupClauseWidth.value = Math.max(c_groupingIconWidth, this.groupClauseWidth.value - widthToRemove);
        }
    };

    private async getTeams() {
        if (this.teamFieldValues.length !== 0) {
            return;
        }
        const teamService = this.context.pageContext.getService<ITfsTeamService>("ITfsTeamService");
        const teams: WebApiTeam[] = await teamService.getAllTeams(this.props.crossProject ? undefined : this.props.projectId);
        const teamsData: TeamsData[] = [];
        if (teams && teams.length > 0) {
            teams.forEach(team => {
                teamsData.push({
                    id: team.id,
                    name: team.name,
                    identityUrl: team.identityUrl,
                    projectId: team.projectId,
                    projectName: team.projectName
                });
            });
            teamsData.sort((t1, t2) => localeIgnoreCaseComparer(t1.projectName + t1.name, t2.projectName + t2.name));
            this.teamFieldValues.value = teamsData;
        }
    }

    private renderAddRemoveClauseIcons = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IQueryEditorItem>,
        tableItem: IQueryEditorItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex}>
                <Button
                    ariaLabel={Resources.InsertNewFilterTooltip}
                    className="add-line-button"
                    iconProps={{ iconName: "Add" }}
                    tooltipProps={{ text: Resources.InsertNewFilterTooltip }}
                    onClick={() => this.onAddRow(rowIndex)}
                />
                <Button
                    ariaLabel={Resources.RemoveFilterTooltip}
                    className="remove-line-button"
                    iconProps={{ iconName: "Cancel" }}
                    tooltipProps={{ text: Resources.RemoveFilterTooltip }}
                    onClick={() => this.onRemoveRow(rowIndex)}
                />
            </SimpleTableCell>
        );
    };

    // GROUP CLAUSE START
    private checkForConsecutiveGrouping = (sortedIndices: number[]) => {
        // you can only group consecutive rows
        const consecutive = sortedIndices.every((rowIndex, i) => i === 0 || sortedIndices[i] === sortedIndices[i - 1] + 1);
        if (consecutive) {
            this.disableGroupClauseButton.value = false;
            this.currentFilterGroup.value = {
                start: sortedIndices[1],
                end: sortedIndices[sortedIndices.length - 1] + 1,
                level: sortedIndices.length - 2
            }; // group indices start from 1
        } else {
            this.disableGroupClauseButton.value = true;
            this.currentFilterGroup.value = { start: -1, end: -1, level: 0 };
        }
    };

    private doesGroupingIntersect = (sortedIndices: number[]) => {
        const start = sortedIndices[0];
        const end = start + sortedIndices.length - 1;
        for (const level of this.allFilterGroups.value.keys()) {
            const groups = this.allFilterGroups.value.get(level);
            if (!groups) {
                continue;
            }
            for (const [index, group] of groups.entries()) {
                const groupEnd = group.end - 1;
                const groupStart = group.start - 1;
                // grouping is invalid if:
                //    - the grouping already exists (end === group end && groupStart === start)
                //    - they overlap & are not nested
                if (
                    (groupEnd === end && groupStart === start) ||
                    (end >= groupStart &&
                        start <= groupEnd &&
                        !((start >= groupStart && end <= groupEnd) || (groupStart >= start && groupEnd <= end)))
                ) {
                    this.disableGroupClauseButton.value = true;
                    this.currentFilterGroup.value = { start: -1, end: -1, level: 0 };
                    return true;
                }
            }
        }

        return false;
    };

    private checkIfSelectedClausesCanBeGrouped = (checked: boolean, rowIndex: number) => {
        if (checked && !this.groupedRowIndices.includes(rowIndex)) {
            this.groupedRowIndices.push(rowIndex);
        } else if (!checked) {
            const index = this.groupedRowIndices.indexOf(rowIndex);
            if (index !== -1) {
                this.groupedRowIndices.splice(index, 1);
            }
        }

        if (this.groupedRowIndices.length > 1) {
            const sortedIndices = [...this.groupedRowIndices].sort((a, b) => a - b);
            if (!this.doesGroupingIntersect(sortedIndices)) {
                this.checkForConsecutiveGrouping(sortedIndices);
            }
        } else {
            this.disableGroupClauseButton.value = true;
            this.currentFilterGroup.value = { start: -1, end: -1, level: 0 };
        }
    };

    private renderGroupClauseCheckbox = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IQueryEditorItem>,
        tableItem: IQueryEditorItem
    ): JSX.Element => {
        const onGroupClauseCheckboxChanged = (checked: boolean) => {
            this.rows.value[rowIndex].groupClause.value = checked;
            this.checkIfSelectedClausesCanBeGrouped(checked, rowIndex);
        };
        const onUngroupButtonClicked = (level: number, indexInLevel: number) => {
            const levelArray = this.allFilterGroups.value.get(level);
            if (levelArray && levelArray.length) {
                if (levelArray.length === 1) {
                    this.allFilterGroups.value.delete(level);

                    // only decrement width when a level is being removed
                    this.groupClauseWidth.value -= c_groupingIconWidth;
                } else {
                    levelArray.splice(indexInLevel, 1);
                }
                this.allFilterGroups.notify(this.allFilterGroups.value, "remove");
            }
            this.updateFilterGroups();
            this.props.setIsDirty(true);
            this.updateCurrentFilterGroupAfterGroupRemoved();
        };

        const getButtons = (): JSX.Element[] | null => {
            const buttonsAndColors = [];
            const keys = Array.from(this.allFilterGroups.value.keys()).sort((a, b) => b - a);
            for (const [levelIndex, level] of keys.entries()) {
                const groups = this.allFilterGroups.value.get(level);
                if (!groups) {
                    return null;
                }
                for (const [index, group] of groups.entries()) {
                    const divClassName = css(
                        "group-clause-button",
                        levelIndex % 2 === 0 ? "group-clause-button-color-2" : "group-clause-button-color-1"
                    );
                    if (group.start === rowIndex + 1) {
                        buttonsAndColors.push(
                            <div className={css("flex-row", "flex-center", divClassName, "justify-center")}>
                                <Button
                                    ariaLabel={Resources.UngroupSelectedClauses}
                                    className={divClassName}
                                    iconProps={{ iconName: "UngroupList", tooltipProps: { text: Resources.UngroupSelectedClauses } }}
                                    onClick={() => onUngroupButtonClicked(level, index)}
                                />
                            </div>
                        );
                    } else if (group.start - 1 <= rowIndex && group.end - 1 >= rowIndex) {
                        buttonsAndColors.push(<div className={divClassName} />);
                    }
                }
            }

            return buttonsAndColors;
        };
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex}>
                <Observer filterGroups={this.allFilterGroups}>
                    {() => {
                        return (
                            <>
                                <Checkbox
                                    ariaLabel={Resources.GroupClauseCheckboxAriaLabel}
                                    checked={tableItem.groupClause}
                                    onChange={(event: any, checked: boolean) => onGroupClauseCheckboxChanged(checked)}
                                />
                                {getButtons()}
                            </>
                        );
                    }}
                </Observer>
            </SimpleTableCell>
        );
    };
    // GROUP CLAUSE END

    private renderAndOrDropdown = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IQueryEditorItem>,
        tableItem: IQueryEditorItem
    ): JSX.Element => {
        const andOrDropdownList: IListBoxItem[] = [
            { id: "and", text: Resources.AndText },
            { id: "or", text: Resources.OrText }
        ];
        const onAndOrSelected = (item: IListBoxItem) => {
            const data = this.rows.value[rowIndex];
            if (data.andOr.value !== item.id) {
                data.andOr.value = item.id;
                data.andOrSelection.select(item.id === "or" ? 1 : 0);
                this.props.setClauseLogicalOperatorState(item.id === "or" ? "OR" : "AND", rowIndex, this.props.clauseType);
                this.props.setIsDirty(true);
            }
        };
        if (rowIndex === 0) {
            return <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex} />;
        }
        this.rows.value[rowIndex].andOr.value === "or"
            ? this.rows.value[rowIndex].andOrSelection.select(1)
            : this.rows.value[rowIndex].andOrSelection.select(0);
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex}>
                <EditableDropdown
                    className="table-dropdown"
                    items={andOrDropdownList}
                    selection={this.rows.value[rowIndex].andOrSelection}
                    onValueChange={(item: IListBoxItem) => onAndOrSelected(item)}
                />
            </SimpleTableCell>
        );
    };

    private renderFieldDropdown = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IQueryEditorItem>,
        tableItem: IQueryEditorItem
    ): JSX.Element => {
        const onFieldSelected = (item: IListBoxItem) => {
            const data = this.rows.value[rowIndex];
            if (data.field.value !== item.id) {
                const index = this.workItemFieldList.value.findIndex(fieldItem => fieldItem.id === item.id);
                if (index !== -1) {
                    data.fieldError.value = false;
                    data.field.value = item.id;
                    data.operatorList.value = this.getListBoxItems(this.getOperators(item.id));

                    const opToSelect = data.field.value === CoreFieldRefNames.Title ? WiqlOperators.OperatorContainsWords : data.operator.value;
                    const opIndex = data.operatorList.value.findIndex(op => op.id.toLowerCase() === opToSelect.toLowerCase());
                    if (opIndex !== -1) {
                        data.operatorSelection.select(opIndex);
                    } else {
                        data.operator.value = data.operatorList.value[0].id;
                        data.operatorSelection.select(0);
                        this.props.setClauseOperatorState(data.operator.value, rowIndex, this.props.clauseType);
                    }
                    this.updateValueListBasedOnOp(rowIndex, item.id, true);
                    this.props.setClauseFieldState(item.text ?? item.id, rowIndex, this.props.clauseType);
                    this.props.setClauseValueState("", rowIndex, this.props.clauseType);
                    this.props.setIsDirty(true);
                } else {
                    data.fieldError.value = true;
                }
            }
        };

        if (tableItem.fieldSelection.selectedCount === 0 || tableItem.fieldSelection.value[0].beginIndex === -1) {
            const index = this.workItemFieldList.value.findIndex(item => item.id === tableItem.field.value);
            if (index !== -1) {
                tableItem.fieldSelection.select(index);
            } else {
                const indexInCrossProjectFields = this.props.fields.findIndex(f => f.referenceName === tableItem.field.value);
                if (indexInCrossProjectFields !== -1 && tableItem.field.value !== CoreFieldRefNames.TeamProject) {
                    tableItem.fieldError.value = true;
                }
                tableItem.fieldSelection.clear();
            }
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex}>
                <FormItem className="full-width" error={tableItem.fieldError}>
                    <EditableDropdown
                        allowFreeform={true}
                        allowTextSelection={true}
                        ariaLabel={Resources.FieldColumn}
                        className="table-dropdown"
                        items={this.workItemFieldList}
                        selection={tableItem.fieldSelection}
                        onValueChange={(item: IListBoxItem) => onFieldSelected(item)}
                        ref={ref => {
                            if (tableItem.isNewItem) {
                                ref?.focus();
                                tableItem.isNewItem = false;
                            }
                        }}
                        required={true}
                        selectedText={
                            tableItem.fieldSelection.selectedCount === 0
                                ? this.props.fields.find(f => f.referenceName === tableItem.field.value)?.name
                                : tableItem.fieldSelection.value[0].beginIndex !== -1
                                ? this.workItemFieldList.value[tableItem.fieldSelection.value[0].beginIndex].text
                                : ""
                        }
                    />
                </FormItem>
            </SimpleTableCell>
        );
    };

    // OPERATOR START
    private getOperators = (fieldRefName: string): string[] => {
        const field = this.props.fields.find(field => field.referenceName === fieldRefName);
        const fieldType = field?.type ?? -1;
        let invariantOps: string[] = [];
        if (field && field.referenceName === CoreFieldRefNames.Tags) {
            // tags is not a real plain text field
            invariantOps.push(...[WiqlOperators.OperatorContains, WiqlOperators.OperatorNotContains]);
        } else {
            switch (+fieldType) {
                case FieldType.String:
                    invariantOps =
                        field && checkFlag(field.flags, FieldFlags.SupportsTextQuery)
                            ? StringWithTextSupportOperators
                            : field && field.referenceName === CoreFieldRefNames.WorkItemType
                            ? StringWithInGroupOperators
                            : StringOperators;
                    break;
                case FieldType.Integer:
                case FieldType.DateTime:
                case FieldType.Double:
                    invariantOps = ComparisonOperators;
                    break;
                case FieldType.PlainText:
                case FieldType.Html:
                    invariantOps = field && checkFlag(field.flags, FieldFlags.SupportsTextQuery) ? TextWithTextSupportOperators : TextOperators;
                    break;
                case FieldType.TreePath:
                    invariantOps = TreePathOperators;
                    break;
                case FieldType.History:
                    invariantOps = field && checkFlag(field.flags, FieldFlags.SupportsTextQuery) ? HistoryWithTextSupportOperators : HistoryOperators;
                    break;
                case FieldType.Guid:
                case FieldType.Boolean:
                    invariantOps = EqualityOperators;
                    break;
                default:
                    invariantOps = [WiqlOperators.OperatorEqualTo];
                    break;
            }
        }

        if (FieldTypesSupportingEver.hasOwnProperty(fieldType) && invariantOps.indexOf(WiqlOperators.OperatorEver) === -1) {
            invariantOps = [...invariantOps, WiqlOperators.OperatorEver];
        }

        const availableOps = getLocalizedOperatorList(invariantOps);
        if (FieldTypesSupportingFieldComparison.hasOwnProperty(fieldType)) {
            invariantOps.forEach(op => {
                if (OperatorsSupportingFieldComparison.hasOwnProperty(op.toUpperCase())) {
                    availableOps.push(getFieldComparisonOperator(op));
                }
            });
        }
        return availableOps;
    };

    private renderOperatorDropdown = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IQueryEditorItem>,
        tableItem: IQueryEditorItem
    ): JSX.Element => {
        const onOperatorSelected = (item: IListBoxItem) => {
            const data = this.rows.value[rowIndex];
            if (data.operator.value !== item.id) {
                const prevOperator = data.operator.value;
                data.operator.value = item.id;
                const operatorSelectionIndex = data.operatorList.value.findIndex(op => op.id === item.id);
                data.operatorSelection.select(operatorSelectionIndex > -1 ? operatorSelectionIndex : 0);
                this.props.setClauseOperatorState(item.id, rowIndex, this.props.clauseType);
                this.props.setIsDirty(true);
                const isIdentity = this.isIdentityField(data.field.value) ?? false;
                const clearValueList: boolean =
                    (data.field.value === CoreFieldRefNames.WorkItemType &&
                        // value lists for work item type's group operators are different from non-group operators
                        ((isGroupOperator(prevOperator) && !isGroupOperator(item.id)) ||
                            (isGroupOperator(item.id) && !isGroupOperator(prevOperator)))) ||
                    // for tree fields, value lists should be cleared if selected value is a macro, and new operator does not support macros
                    (data.field.value === CoreFieldRefNames.AreaPath &&
                        AreaPathOperatorsSupportingMacros.includes(getInvariantOperator(prevOperator)) &&
                        !AreaPathOperatorsSupportingMacros.includes(getInvariantOperator(item.id)) &&
                        data.value.value.toLowerCase().startsWith(WiqlOperators.MacroTeamAreas.toLowerCase())) ||
                    (data.field.value === CoreFieldRefNames.IterationPath &&
                        IterationPathOperatorsSupportingMacros.includes(getInvariantOperator(prevOperator)) &&
                        !IterationPathOperatorsSupportingMacros.includes(getInvariantOperator(item.id)) &&
                        data.value.value.toLowerCase().startsWith(WiqlOperators.MacroCurrentIteration.toLowerCase())) ||
                    (isIdentity &&
                        ((isGroupOperator(prevOperator) && !isGroupOperator(item.id)) ||
                            (isGroupOperator(item.id) && !isGroupOperator(prevOperator)) ||
                            (isMultipleValueOperator(prevOperator) && !isMultipleValueOperator(item.id)) ||
                            (isMultipleValueOperator(item.id) && !isMultipleValueOperator(prevOperator)) ||
                            (isContainsNotContainsOperator(prevOperator) && !isContainsNotContainsOperator(item.id)) ||
                            (isContainsNotContainsOperator(item.id) && !isContainsNotContainsOperator(prevOperator))));
                this.updateValueListBasedOnOp(rowIndex, data.field.value, clearValueList);
            }
        };

        if (tableItem.operatorList.value.length == 0) {
            tableItem.operatorList.value = this.getOperators(tableItem.field.value).map(op => {
                return { id: op, text: op };
            });
        }

        if (tableItem.operatorSelection.selectedCount === 0) {
            const selectionIndex = tableItem.operatorList.value.findIndex(op => op.id === tableItem.operator.value);
            tableItem.operatorSelection.select(selectionIndex > -1 ? selectionIndex : 0);
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex}>
                <EditableDropdown
                    className="table-dropdown"
                    items={this.rows.value[rowIndex].operatorList}
                    selection={tableItem.operatorSelection}
                    onValueChange={(item: IListBoxItem) => onOperatorSelected(item)}
                    selectedText={this.rows.value[rowIndex].operator}
                />
            </SimpleTableCell>
        );
    };
    // OPERATOR END

    // VALUES START
    private getFieldType = (fieldRefName: string): FieldType | undefined => {
        if (fieldRefName === CoreFieldRefNames.TeamProject) {
            return FieldType.Internal;
        }
        return this.props.fields.find(field => field.referenceName === fieldRefName)?.type;
    };

    private isIdentityField = (fieldRefName: string): boolean | undefined => {
        return this.props.fields.find(field => field.referenceName === fieldRefName)?.isIdentity;
    };

    private updateValueSelection = (rowIndex: number, preserveValueSelection?: boolean) => {
        const data = this.rows.value[rowIndex];
        const index = data.valueList.value.findIndex(v => v.id === data.value.value || v.text === data.value.value);
        if (index !== -1) {
            data.valueSelection.select(index);
        } else if (!preserveValueSelection) {
            data.valueSelection.clear();
            data.value.value = "";
        }
    };

    private updateTeamSelection = (rowIndex: number) => {
        const data = this.rows.value[rowIndex];
        const pathValue = data.valuePathTeam.value;
        const teamIndex = this.teamFieldValues.value.findIndex(v => v.id === pathValue.substring(pathValue.indexOf(":") + 1, pathValue.indexOf(">")));
        if (teamIndex !== -1) {
            data.valuePathTeamSelection.select(teamIndex);
        } else {
            data.valuePathTeam.value = "";
            data.valuePathTeamSelection.clear();
        }
    };

    private async getAllowedValues(fieldRefName: string, projectId?: string): Promise<string[] | undefined> {
        const fieldService = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)
                    ?  this.context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2")
                    : this.context.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        return await fieldService.getAllowedValues(fieldRefName, this.props.projectId);
    }

    private async getTagsSuggestedValues() {
        if (this.fieldValuesByRefName.hasOwnProperty(CoreFieldRefNames.Tags) || !this.hasTags) {
            return;
        }
        const tagsData = await this.getAllowedValues(CoreFieldRefNames.Tags, this.props.projectId);
        if (tagsData && tagsData.length !== 0) {
            const tagsListItems: IListBoxItem[] = this.getListBoxItems(tagsData);
            this.fieldRefNameToValueListMap[CoreFieldRefNames.Tags] = new ObservableArray(tagsListItems);
        } else {
            this.hasTags = false;
        }
    }

    private async getColumnSuggestedValues() {
        if (this.fieldValuesByRefName.hasOwnProperty(CoreFieldRefNames.BoardColumn)) {
            return;
        }
        const columnsData = await this.workRestClient.getColumnSuggestedValues(this.props.crossProject ? undefined : this.props.projectId);
        if (columnsData && columnsData.length !== 0) {
            const columnsListItems: IListBoxItem[] = this.getListBoxItems(columnsData.map(data => data.name));
            this.fieldValuesByRefName[CoreFieldRefNames.BoardColumn] = new ObservableArray(columnsListItems);
        }
    }

    private async getRowSuggestedValues() {
        if (this.fieldValuesByRefName.hasOwnProperty(CoreFieldRefNames.BoardLane) || !this.hasRows) {
            return;
        }
        const rowsData = await this.workRestClient.getRowSuggestedValues(this.props.crossProject ? undefined : this.props.projectId);
        if (rowsData && rowsData.length !== 0) {
            const rowsListItems: IListBoxItem[] = this.getListBoxItems(rowsData.map(data => data.name));
            this.fieldValuesByRefName[CoreFieldRefNames.BoardLane] = new ObservableArray(rowsListItems);
        } else {
            this.hasRows = false;
        }
    }

    private async loadProjects() {
        if (this.fieldValuesByRefName.hasOwnProperty(CoreFieldRefNames.TeamProject)) {
            return;
        }
        const projectsService = this.context.pageContext.getService<ITfsProjectService>("ITfsProjectService");
        const projects = await projectsService.getAllProjectsLoadedPromise();
        if (projects && projects.length > 0) {
            const projListItems: IListBoxItem[] = this.getListBoxItems(projects.map(proj => proj.name));
            this.fieldRefNameToValueListMap_CrossProject[CoreFieldRefNames.TeamProject] = new ObservableArray(projListItems);
        }
    }

    private updateValueListBasedOnOp = (rowIndex: number, fieldRefName: string, clearValueList: boolean) => {
        const data = this.rows.value[rowIndex];
        const operator: string = data.operator.value;
        if (clearValueList) {
            data.value.value = "";
            data.valuePathTeam.value = "";
            data.valueSelection.clear();
            this.props.setClauseValueState("", rowIndex, this.props.clauseType);
        }
        if (fieldRefName === CoreFieldRefNames.AreaPath || fieldRefName === CoreFieldRefNames.IterationPath) {
            if (fieldRefName === CoreFieldRefNames.AreaPath && AreaPathOperatorsSupportingMacros.indexOf(getInvariantOperator(operator)) !== -1) {
                data.valueList.value = this.getListBoxItems([getLocalizedOperator(WiqlOperators.MacroTeamAreas)], true);
            } else if (
                fieldRefName === CoreFieldRefNames.IterationPath &&
                IterationPathOperatorsSupportingMacros.indexOf(getInvariantOperator(operator)) !== -1
            ) {
                data.valueList.value = this.getListBoxItems(IterationPathMacros, true);
            } else {
                data.valueList.value = [];
            }

            data.valueDropdownInput.value = InputMode.Tree;
            // fetch team values
            this.getTeams().then(() => {
                this.updateTeamSelection(rowIndex);
            });
        } else if (this.getFieldType(fieldRefName) === FieldType.Boolean) {
            data.valueList.value = this.getListBoxItems(["True", "False"]);
            data.valueDropdownInput.value = InputMode.EditableDropdown;
            this.updateValueSelection(rowIndex);
        } else if (isFieldComparisonOperator(operator)) {
            const fieldType = this.getFieldType(fieldRefName);
            data.valueList.value = this.props.fields
                .filter(field => field.isInCurrentProject !== null && field.isInCurrentProject && field.type === fieldType)
                .map(field => {
                    return { id: field.referenceName, text: field.name } as IListBoxItem;
                });
            data.valueDropdownInput.value = InputMode.EditableDropdown;
            this.updateValueSelection(rowIndex);
        } else if (fieldRefName === CoreFieldRefNames.Tags) {
            this.getTagsSuggestedValues().then(() => {
                if (this.fieldRefNameToValueListMap.hasOwnProperty(fieldRefName)) {
                    data.valueList.value = this.fieldRefNameToValueListMap[fieldRefName].value;
                    data.valueDropdownInput.value = InputMode.EditableDropdown;
                    this.updateValueSelection(rowIndex, true);
                } else {
                    data.valueDropdownInput.value = InputMode.TextField;
                }
            });
        } else if (fieldRefName === CoreFieldRefNames.BoardColumn) {
            this.getColumnSuggestedValues().then(() => {
                if (this.fieldRefNameToValueListMap.hasOwnProperty(fieldRefName)) {
                    data.valueList.value = this.fieldRefNameToValueListMap[fieldRefName].value;
                    data.valueDropdownInput.value = InputMode.EditableDropdown;
                    this.updateValueSelection(rowIndex, true);
                } else {
                    data.valueDropdownInput.value = InputMode.TextField;
                }
            });
        } else if (fieldRefName === CoreFieldRefNames.BoardLane) {
            this.getRowSuggestedValues().then(() => {
                if (this.fieldRefNameToValueListMap.hasOwnProperty(fieldRefName)) {
                    data.valueList.value = this.fieldRefNameToValueListMap[fieldRefName].value;
                    data.valueDropdownInput.value = InputMode.EditableDropdown;
                    this.updateValueSelection(rowIndex, true);
                } else {
                    data.valueDropdownInput.value = InputMode.TextField;
                }
            });
        } else if (this.isIdentityField(fieldRefName)) {
            this.setAdditionalEntriesToPeoplePicker(fieldRefName);
            if (isMultipleValueOperator(operator)) {
                data.valueDropdownInput.value = InputMode.IdentityPicker;
            } else if (isContainsNotContainsOperator(operator)) {
                data.valueDropdownInput.value = InputMode.TextField;
            } else {
                data.valueDropdownInput.value = InputMode.IdentityPickerDropdown;
            }
        } else if (this.getFieldType(fieldRefName) === FieldType.DateTime) {
            const items = this.getListBoxItems(DateMacros);
            items.unshift({ id: c_customDateId, text: "Custom date..." }, { id: "custom-date-separator", type: ListBoxItemType.Divider });
            data.valueList.value = items;
            data.valueDropdownInput.value = InputMode.DatePickerDropdown;
            this.updateValueSelection(rowIndex, true);
        } else if (fieldRefName === CoreFieldRefNames.TeamProject) {
            this.loadProjects().then(() => {
                if (this.fieldValuesByRefName.hasOwnProperty(fieldRefName)) {
                    data.valueList.value = this.fieldValuesByRefName[fieldRefName].value;
                    data.valueDropdownInput.value = InputMode.EditableDropdown;
                    this.updateValueSelection(rowIndex, true);
                } else {
                    data.valueDropdownInput.value = InputMode.TextField;
                }
            });
        } else if (fieldRefName === CoreFieldRefNames.Id) {
            if (isMultipleValueOperator(operator)) {
                data.valueList.value = this.getListBoxItems(IdInNotInMacros);
                data.valueDropdownInput.value = InputMode.EditableDropdown;
                this.updateValueSelection(rowIndex, true);
            } else {
                data.valueDropdownInput.value = InputMode.TextField;
            }
        } else {
            if (isNonNullableField(fieldRefName)) {
                data.valueList.value = [{ id: "[Any]", text: Resources.AnyOptionText }];
            } else {
                data.valueList.value = [];
            }
            if (fieldRefName === CoreFieldRefNames.WorkItemType && isGroupOperator(operator)) {
                if (this.workItemTypeCategories.length > 0) {
                    data.valueList.push(...this.getListBoxItems(this.workItemTypeCategories.value.map(c => c.referenceName)));
                    data.valueDropdownInput.value = InputMode.EditableDropdown;
                    this.updateValueSelection(rowIndex, true);
                } else {
                    const categories: Promise<WorkItemTypeCategory[]> = this.witRestClient.getWorkItemTypeCategories(this.props.projectId);
                    if (categories instanceof Promise) {
                        categories.then(c => {
                            if (c && c.length) {
                                this.workItemTypeCategories.value = c;
                                data.valueList.push(...this.getListBoxItems(c.map(cat => cat.referenceName)));
                                data.valueDropdownInput.value = InputMode.EditableDropdown;
                                this.updateValueSelection(rowIndex, true);
                            }
                        });
                    }
                }
            } else {
                if (this.fieldValuesByRefName.hasOwnProperty(fieldRefName)) {
                    data.valueList.push(...this.fieldValuesByRefName[fieldRefName].value);
                    data.valueDropdownInput.value = InputMode.EditableDropdown;
                    this.updateValueSelection(rowIndex, true);
                } else {
                    const allowedValues: Promise<string[] | undefined> = this.getAllowedValues(
                        fieldRefName,
                        this.props.crossProject ? undefined : this.props.projectId
                    );
                    if (allowedValues instanceof Promise) {
                        allowedValues.then(v => {
                            if (v && v.length) {
                                data.valueList.push(...this.getListBoxItems(v));
                                this.fieldValuesByRefName[fieldRefName] = new ObservableArray(this.getListBoxItems(v));
                                data.valueDropdownInput.value = InputMode.EditableDropdown;

                                this.updateValueSelection(rowIndex, true);
                            } else {
                                data.valueDropdownInput.value = InputMode.TextField;
                            }
                        });
                    }
                }
            }
        }
    };

    private renderValueDropdown = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IQueryEditorItem>,
        tableItem: IQueryEditorItem
    ): JSX.Element => {
        const onValueSelected = (item: IListBoxItem) => {
            if (!item) {
                onValueChanged(undefined, "");
                return;
            }

            const data = this.rows.value[rowIndex];

            if (data.value.value !== item.id) {
                data.value.value = item.id;
                const selectionIndex = data.valueList.value.findIndex(v => v.id === item.id);
                data.valueSelection.select(selectionIndex > -1 ? selectionIndex : 0);
                let currValue =
                    this.props.clauseType === ClauseType.Tree
                        ? this.props.editInfo.treeTargetFilter.clauses[rowIndex].value
                        : this.props.clauseType === ClauseType.Link
                        ? this.props.editInfo.linkTargetFilter.clauses[rowIndex].value
                        : this.props.editInfo.sourceFilter.clauses[rowIndex].value;

                if (item.text) {
                    if (item.id.startsWith(WiqlOperators.MacroStart)) {
                        currValue = item.id;
                    } else {
                        // not a macro
                        currValue = item.text;
                    }
                    this.props.setClauseValueState(currValue, rowIndex, this.props.clauseType);
                    this.props.setIsDirty(true);
                }
            }
        };
        const onValueChanged = (event: any, value: string) => {
            this.rows.value[rowIndex].value.value = value;
            this.props.setClauseValueState(value, rowIndex, this.props.clauseType);
            this.props.setIsDirty(true);
        };
        const onDateFieldValueChange = (item?: IListBoxItem) => {
            if (!item) {
                return;
            }
            if (item.id === c_customDateId) {
                const oldValue = tableItem.value.value;
                tableItem.value.value = item.text ?? item.id;
                const layoutManager = this.context.pageContext!.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => (
                    <DatePickerDialog
                        onDismiss={(date?) => {
                            // If no date was selected
                            if (!date) {
                                if (oldValue) {
                                    const prevSelectionIndex = tableItem.value.value
                                        ? tableItem.valueList.value.findIndex(listValue => tableItem.value.value === listValue.text)
                                        : -1;
                                    if (prevSelectionIndex !== -1) {
                                        // And previous selection, select previous item
                                        tableItem.valueSelection.select(prevSelectionIndex);
                                    } else {
                                        tableItem.valueSelection.clear();
                                    }
                                    tableItem.value.value = oldValue;
                                }
                            } else {
                                tableItem.value.value = localeDateFormat(date, "d");
                                this.props.setClauseValueState(date.toISOString(), rowIndex, this.props.clauseType);
                                this.props.setIsDirty(true);
                            }
                            onDismiss();
                        }}
                    />
                ));
            } else {
                tableItem.value.value = item.text ?? item.id;
                this.props.setClauseValueState(tableItem.value.value, rowIndex, this.props.clauseType);
                this.props.setIsDirty(true);
            }
        };
        const setTeamValueInMacros = (team: string) => {
            let clauseValue =
                this.props.clauseType === ClauseType.Tree
                    ? this.props.editInfo.treeTargetFilter.clauses[rowIndex].value
                    : this.props.clauseType === ClauseType.Link
                    ? this.props.editInfo.linkTargetFilter.clauses[rowIndex].value
                    : this.props.editInfo.sourceFilter.clauses[rowIndex].value;
            if (this.rows.value[rowIndex].field.value === CoreFieldRefNames.IterationPath) {
                const teamAndOffset = parseCurrentIteration(clauseValue, true);
                clauseValue = `${WiqlOperators.MacroCurrentIteration}(\'${team}\')`;
                if (teamAndOffset && teamAndOffset.offset !== 0) {
                    clauseValue += `${teamAndOffset.offset > 0 ? " + " : " - "}${Math.abs(teamAndOffset.offset)}`;
                }
            } else {
                clauseValue = `${WiqlOperators.MacroTeamAreas}(\'${team}\')`;
            }
            this.props.setClauseValueState(clauseValue, rowIndex, this.props.clauseType);
            this.props.setIsDirty(true);
        };
        const onTeamValueSelected = (item: IListBoxItem) => {
            const index = this.teamFieldValues.value.findIndex(v => v.id === item.id);
            const teamIdentifier = `${item.text} <id:${item.id}>`;
            this.rows.value[rowIndex].valuePathTeam.value = teamIdentifier;
            this.rows.value[rowIndex].valuePathTeamSelection.select(index > -1 ? index : 0);
            setTeamValueInMacros(teamIdentifier);
        };
        const getTeamListBoxItems = (teamsData: TeamsData[]): IListBoxItem[] => {
            return teamsData.map(team => {
                return {
                    id: team.id,
                    text: `[${team.projectName}]\\${team.name}`,
                    iconProps: {
                        render: () => {
                            return (
                                <Persona
                                    className="padding-right-8"
                                    identity={{ displayName: team.name, id: team.id, imageUrl: team.identityUrl }}
                                    size={PersonaSize.size20}
                                />
                            );
                        }
                    }
                } as IListBoxItem;
            });
        };

        const setPath = (path: string) => {
            const data = this.rows.value[rowIndex];
            data.value.value = path;
            let value = path;

            if (
                isValidMacro(path, /** localized */ false) &&
                data.valuePathTeam.value.length !== 0 &&
                data.valuePathTeamSelection.selectedCount !== 0
            ) {
                if (
                    localeIgnoreCaseComparer(data.value.value, WiqlOperators.MacroCurrentIteration) === 0 ||
                    localeIgnoreCaseComparer(data.value.value, WiqlOperators.MacroTeamAreas) === 0
                ) {
                    // @teamAreas or @currentIteration
                    value = path + `(\'${data.valuePathTeam.value}\')`;
                } else {
                    // @currentIteration + 1 or @currentIteration - 1
                    value =
                        WiqlOperators.MacroCurrentIteration +
                        `(\'${data.valuePathTeam.value}\')` +
                        path.substring(WiqlOperators.MacroCurrentIteration.length);
                }
            }

            this.props.setClauseValueState(value, rowIndex, this.props.clauseType);
            this.props.setIsDirty(true);
        };

        const getLocalizedPathMacro = (operator: string) => {
            return this.rows.value[rowIndex].valueList.value.find(value => value.id === operator)?.text ?? getLocalizedOperator(operator);
        };
        const getUniqueStringForIIdentityObject = (identity: IIdentity | undefined): string => {
            if (!identity) {
                return "";
            }

            if (localeIgnoreCaseComparer(identity.entityId, WiqlOperators.MacroMe) === 0) {
                return WiqlOperators.MacroMe;
            } else if (identity?.entityType === "User") {
                return `${identity?.displayName} <${identity?.mail}>`;
            } else if (identity?.entityType === "ServicePrincipal") {
                return `${identity?.displayName} <${identity?.originId}>`;
            } else if (isAADGroupIdentity(identity)) {
                return getAADGroupName(identity);
            } else {
                return `${identity?.displayName} <id:${identity?.localId}>`;
            }
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={tableItem.id + "-col-" + columnIndex}>
                <Observer
                    fieldName={this.rows.value[rowIndex].field}
                    valueList={this.rows.value[rowIndex].valueList}
                    inputType={this.rows.value[rowIndex].valueDropdownInput}
                    value={this.rows.value[rowIndex].value}
                >
                    {(props: { fieldName: string; valueList: IListBoxItem[]; inputType: InputMode; value: string }) =>
                        props.inputType === InputMode.EditableDropdown || props.inputType === InputMode.DatePickerDropdown ? (
                            <div className="table-dropdown">
                                <EditableDropdown
                                    allowFreeform={true}
                                    allowTextSelection={true}
                                    ariaLabel={Resources.ValueColumn}
                                    className="table-dropdown"
                                    items={tableItem.valueList}
                                    selection={tableItem.valueSelection}
                                    disabled={props.fieldName !== "" ? false : true}
                                    onValueChange={(item: IListBoxItem) =>
                                        props.inputType === InputMode.DatePickerDropdown ? onDateFieldValueChange(item) : onValueSelected(item)
                                    }
                                    selectedText={tableItem.value}
                                />
                            </div>
                        ) : props.inputType === InputMode.IdentityPickerDropdown ? (
                            <Observer operator={this.rows.value[rowIndex].operator}>
                                {(idProps: { operator: string }) => (
                                    <IdentityPickerDropdown
                                        className="table-dropdown"
                                        onChange={identity => {
                                            this.rows.value[rowIndex].valueIdentity.value = identity;
                                            const idValue = getUniqueStringForIIdentityObject(identity);
                                            this.rows.value[rowIndex].value.value = idValue;
                                            this.props.setClauseValueState(idValue, rowIndex, this.props.clauseType);
                                            this.props.setIsDirty(true);
                                        }}
                                        pickerProvider={isGroupOperator(idProps.operator) ? this.groupPickerProvider : this.pickerProvider}
                                        value={this.rows.value[rowIndex].valueIdentity}
                                    />
                                )}
                            </Observer>
                        ) : props.inputType === InputMode.IdentityPicker ? (
                            <IdentityPicker
                                className="table-dropdown"
                                pickerProvider={this.pickerProvider}
                                selectedIdentities={this.rows.value[rowIndex].valueIdentityList}
                                onIdentityAdded={identity => {
                                    const idValue = getUniqueStringForIIdentityObject(identity);
                                    const currValue = this.rows.value[rowIndex].value.value;
                                    if (!this.rows.value[rowIndex].value.value.toLowerCase().includes(idValue.toLowerCase())) {
                                        currValue.length > 0
                                            ? (this.rows.value[rowIndex].value.value = `${currValue},${idValue}`)
                                            : (this.rows.value[rowIndex].value.value = idValue);
                                        this.rows.value[rowIndex].valueIdentityList.push(identity);
                                    }
                                    this.props.setClauseValueState(this.rows.value[rowIndex].value.value, rowIndex, this.props.clauseType);
                                    this.props.setIsDirty(true);
                                }}
                                onIdentityRemoved={identity => {
                                    const idValue = getUniqueStringForIIdentityObject(identity);
                                    let currValue = this.rows.value[rowIndex].value.value.split(",");
                                    currValue = currValue.filter(value => value.toLowerCase() !== idValue.toLowerCase());
                                    this.rows.value[rowIndex].value.value = currValue.join(",");
                                    this.rows.value[rowIndex].valueIdentityList.value = this.rows.value[rowIndex].valueIdentityList.value.filter(
                                        ({ entityId, displayName }: IIdentity) => {
                                            return entityId !== "" ? entityId !== identity.entityId : displayName !== identity.displayName;
                                        }
                                    );
                                    this.props.setClauseValueState(this.rows.value[rowIndex].value.value, rowIndex, this.props.clauseType);
                                    this.props.setIsDirty(true);
                                }}
                                onIdentitiesRemoved={identities => {
                                    const idValues: string[] = [];
                                    identities.forEach(identity => {
                                        const idValue = getUniqueStringForIIdentityObject(identity);
                                        if (idValue.length > 0) {
                                            idValues.push(idValue);
                                        }
                                    });
                                    let currValue = this.rows.value[rowIndex].value.value.split(",");
                                    currValue = currValue.filter(value => idValues.filter(idValue => idValue === value).length === 0);
                                    this.rows.value[rowIndex].value.value = currValue.join(",");
                                    this.rows.value[rowIndex].valueIdentityList.value = this.rows.value[rowIndex].valueIdentityList.value.filter(
                                        (entity: IIdentity) => identities.filter(item => item.entityId === entity.entityId).length === 0
                                    );
                                    this.props.setClauseValueState(this.rows.value[rowIndex].value.value, rowIndex, this.props.clauseType);
                                    this.props.setIsDirty(true);
                                }}
                            />
                        ) : props.inputType === InputMode.Tree ? (
                            <div className="table-dropdown">
                                {props.fieldName === CoreFieldRefNames.AreaPath ? (
                                    <AreaPathDropdown
                                        allowFreeform
                                        className="table-dropdown"
                                        macros={props.valueList}
                                        onSetPath={setPath}
                                        selectedText={
                                            props.value.startsWith(WiqlOperators.MacroStart) ? getLocalizedPathMacro(props.value) : props.value
                                        }
                                    />
                                ) : (
                                    <IterationDropdown
                                        allowFreeform
                                        className="table-dropdown"
                                        macros={props.valueList}
                                        onSetPath={setPath}
                                        selectedText={
                                            props.value.startsWith(WiqlOperators.MacroStart) ? getLocalizedPathMacro(props.value) : props.value
                                        }
                                    />
                                )}
                                <Observer teamsData={this.teamFieldValues}>
                                    {(innerProps: { teamsData: TeamsData[] }) =>
                                        (isCurrentIterationMacro(props.value, /** localized */ false) ||
                                            isTeamAreasMacro(props.value, /** localized */ false)) && (
                                            <Dropdown
                                                className="table-dropdown margin-top-4"
                                                dismissOnSelect={true}
                                                items={getTeamListBoxItems(innerProps.teamsData)}
                                                selection={tableItem.valuePathTeamSelection}
                                                onSelect={(event: any, item: IListBoxItem) => onTeamValueSelected(item)}
                                            />
                                        )
                                    }
                                </Observer>
                            </div>
                        ) : (
                            <TextField containerClassName="table-dropdown" onChange={onValueChanged} value={props.value} />
                        )
                    }
                </Observer>
            </SimpleTableCell>
        );
    };
    // VALUES END

    private getListBoxItems = (options: string[], useInvariantOperator?: boolean): IListBoxItem[] => {
        return options.map(option => {
            return { id: useInvariantOperator ? getInvariantOperator(option) : option, text: option } as IListBoxItem;
        });
    };

    private resetSelectedClauses = () => {
        this.rows.value.forEach((row, index) => {
            if (this.groupedRowIndices.includes(index)) {
                row.groupClause.value = false;
            }
        });
        this.groupedRowIndices = [];
        this.disableGroupClauseButton.value = true;
    };

    private modifyGroupedClausesOnAddRow = (index: number) => {
        this.allFilterGroups.value.forEach((value: IFilterGroup[], key: number) => {
            value.forEach(group => {
                if (index >= group.start && index <= group.end) {
                    if (index === group.start) {
                        group.start++;
                    }
                    // increase length of grouped clauses by 1
                    group.end++;
                } else if (index < group.start) {
                    // shift group down by 1
                    group.start++;
                    group.end++;
                }
            });
        });
        this.updateFilterGroups();
    };

    private onAddRow = (rowIndex?: number) => {
        const selection = new DropdownSelection();
        selection.select(0);
        this.resetSelectedClauses();

        const newRow = { ...getDefaultRow(), isNewItem: true };
        if (rowIndex !== undefined) {
            this.modifyGroupedClausesOnAddRow(rowIndex + 1); // group indices start from 1
            this.rows.splice(rowIndex, 0, newRow);
        } else {
            this.rows.push(newRow);
        }
        this.props.setStateAddRow(this.props.clauseType, rowIndex);
        this.props.setIsDirty(true);
        this.resetClausesIndexes();
    };

    private modifyGroupedClausesOnRemoveRow = (index: number) => {
        this.allFilterGroups.value.forEach((value: IFilterGroup[], key: number) => {
            for (let i = value.length - 1; i >= 0; i--) {
                if (index >= value[i].start && index <= value[i].end) {
                    if (value[i].end - value[i].start > 1) {
                        // reduce length of grouped clause by 1
                        value[i].end--;
                    } else {
                        // group cannot contain a single element, so remove the grouping
                        value.splice(i, 1);
                    }
                } else if (index < value[i].start) {
                    // shift group up by 1
                    value[i].start--;
                    value[i].end--;
                }
            }
        });
        this.updateFilterGroups();
    };

    resetClausesIndexes() {
        const filters = [this.props.editInfo.linkTargetFilter, this.props.editInfo.sourceFilter, this.props.editInfo.treeTargetFilter];

        filters.forEach(filter => {
            if (!filter) {
                return;
            }

            const clauseCount = filter.clauses.length;
            for (var i = 0; i < clauseCount; i++) {
                filter.clauses[i].index = i;
            }
        });
    }

    private onRemoveRow = (rowIndex: number) => {
        this.resetSelectedClauses();
        this.modifyGroupedClausesOnRemoveRow(rowIndex + 1); // group indices start from 1
        this.rows.splice(rowIndex, 1);
        this.props.setStateRemoveRow(this.props.clauseType, rowIndex);
        this.props.setIsDirty(true);
        this.resetClausesIndexes();
    };

    private onSizeColumn = (event: MouseEvent | KeyboardEvent, index: number, width: number) => {
        this.columns[index].width.value = width;
    };

    private onClickGroupClause = () => {
        const levelArray = this.allFilterGroups.value.get(this.currentFilterGroup.value.level);
        if (levelArray) {
            levelArray.push(this.currentFilterGroup.value);
        } else {
            this.allFilterGroups.value.set(this.currentFilterGroup.value.level, [this.currentFilterGroup.value]);

            // only increment width when it's a new level
            this.groupClauseWidth.value += c_groupingIconWidth;
        }
        this.allFilterGroups.notify(this.allFilterGroups.value, "add");
        this.rows.value.forEach(row => {
            row.groupClause.value = false;
        });

        this.currentFilterGroup.value = { start: -1, end: -1, level: 0 };
        this.groupedRowIndices = [];
        this.disableGroupClauseButton.value = true;
        this.updateFilterGroups();
        this.props.setIsDirty(true);
    };

    private updateFilterGroups = () => {
        const filter =
            this.props.clauseType === ClauseType.Source
                ? this.props.editInfo.sourceFilter
                : this.props.clauseType === ClauseType.Link
                ? this.props.editInfo.linkTargetFilter
                : this.props.editInfo.treeTargetFilter;
        filter.groups = Array.from(this.allFilterGroups.value.values()).flat();
        this.props.setFilterState(filter, this.props.clauseType);
    };

    /**
     * Update the currentFilterGroup after a grouping is removed - we may have been blocking
     * a group from being created because it intersected with the removed grouping. Now the selection
     * might be valid and the user should be able to click the group button
     */
    private updateCurrentFilterGroupAfterGroupRemoved() {
        this.groupedRowIndices = this.rows.value
            .map((item, index) => item.groupClause.value && index)
            .filter(item => typeof item === "number") as number[];
        if (this.groupedRowIndices.length > 0) {
            const lastRowIndex = this.groupedRowIndices[this.groupedRowIndices.length - 1];
            this.checkIfSelectedClausesCanBeGrouped(/* checked */ true, lastRowIndex);
        }
    }
    /**
     * Set additional non-identity values in the people picker dropdown.
     *
     * @param fieldRefName The name of field to search allowed values for.
     */
    private setAdditionalEntriesToPeoplePicker(fieldRefName: string) {
        const allowedValues: Promise<string[] | undefined> = this.getAllowedValues(
            fieldRefName,
            this.props.crossProject ? undefined : this.props.projectId
        );
        if (allowedValues instanceof Promise) {
            allowedValues.then(v => {
                if (v && v.length) {
                    this.pickerProvider.setAdditionalEntries?.(v);
                }
            });
        }
    }

    private renderHeaderCell = (columnIndex: number, tableColumn: ITableColumn<IQueryEditorItem>): JSX.Element => {
        return (
            <TableHeaderCell key={"table-header" + columnIndex} column={tableColumn} columnIndex={columnIndex}>
                <Observer disabled={this.disableGroupClauseButton}>
                    {(props: { disabled: boolean }) => (
                        <Button
                            ariaLabel={Resources.GroupSelectedClauses}
                            className="group-clause-button"
                            disabled={props.disabled}
                            iconProps={{ iconName: "RowsGroup", tooltipProps: { text: Resources.GroupSelectedClauses } }}
                            onClick={() => this.onClickGroupClause()}
                            subtle={true}
                        />
                    )}
                </Observer>
            </TableHeaderCell>
        );
    };

    private columns = [
        {
            id: "add-remove",
            onSize: this.onSizeColumn,
            renderCell: this.renderAddRemoveClauseIcons,
            readonly: true,
            width: new ObservableValue(70)
        },
        {
            id: "group-clause-checkbox",
            onSize: this.onSizeColumn,
            renderCell: this.renderGroupClauseCheckbox,
            renderHeaderCell: this.renderHeaderCell,
            readonly: true,
            width: this.groupClauseWidth
        },
        {
            id: "and/or",
            name: Resources.AndOrColumn,
            onSize: this.onSizeColumn,
            renderCell: this.renderAndOrDropdown,
            readonly: true,
            width: new ObservableValue(100)
        },
        {
            id: "field",
            name: Resources.FieldColumn,
            onSize: this.onSizeColumn,
            renderCell: this.renderFieldDropdown,
            readonly: false,
            width: new ObservableValue(-25),
            required: true
        },
        {
            id: "operator",
            name: Resources.OperatorColumn,
            onSize: this.onSizeColumn,
            renderCell: this.renderOperatorDropdown,
            readonly: true,
            width: new ObservableValue(-15)
        },
        {
            id: "value",
            name: Resources.ValueColumn,
            onSize: this.onSizeColumn,
            renderCell: this.renderValueDropdown,
            readonly: true,
            width: new ObservableValue(-60)
        }
    ];
}
